import { useEffect, useId, useRef, useState } from "react";
import L from "leaflet";
import "leaflet-gesture-handling";
import { CarFront, Crosshair, LocateFixed, MapPin, Minus, Plus, TrainFront, Users, X } from "lucide-react";
import { RefreshButton } from "./RefreshButton";
import "leaflet/dist/leaflet.css";
import "./PickupMaps.css";
import { isTwoFingerPan, LAST_KNOWN_FIX_MS, mapFix, mapFixAge, PICKUP_MAP_AREA, pickupZonesFor, touchMidpoint, type MapFix, type PickupZoneId, type ScreenPoint } from "../../core/pickup-map";
import { BART_STATIONS, COLLEGE_PREP_BOUNDARY } from "../../core/profiles";
import type { FollowStop } from "../../core/follow";
import { usePickupMap } from "../usePickupMap";
import { useMapLocationRequest } from "../useMapLocation";
import { addBartNetwork } from "./BartNetwork";

type View = "location" | "pickup";
type Focus = "both" | "driver" | "rider";
const area = (stationId: string | null) => stationId && stationId !== "CIVC" && BART_STATIONS[stationId]
	? stationId === "GLEN" ? L.latLngBounds([[37.73275, -122.43435], [37.73365, -122.43315]]) : L.latLng(BART_STATIONS[stationId].lat, BART_STATIONS[stationId].lon).toBounds(500)
	: L.latLngBounds(PICKUP_MAP_AREA.map(point => [point.lat, point.lon] as L.LatLngTuple));
const zoneBounds = (stationId: string | null) => stationId === "CIVC"
	? L.latLngBounds(pickupZonesFor(stationId).flatMap(zone => zone.boundary.map(point => [point.lat, point.lon] as L.LatLngTuple)))
	: area(stationId);

function fitPickupArea(map: L.Map, stationId: string | null) {
	map.stop();
	map.setMinZoom(2);
	map.fitBounds(zoneBounds(stationId), { padding: [16, 16], animate: false });
	map.setMinZoom(map.getZoom());
}

declare module "leaflet" {
	interface MapOptions {
		gestureHandling?: boolean;
		gestureHandlingOptions?: { text: { touch: string; scroll: string; scrollMac: string } };
	}
}

function fixLabel(position: MapFix | null, nowMs: number, maxAgeMs: number): string {
	if (position?.source === "manual-test") return "Manual test coordinate";
	const age = mapFixAge(position, nowMs, maxAgeMs);
	if (age === "missing" || !position) return "No recent position";
	const seconds = Math.max(0, Math.floor((nowMs - position.fixAtMs) / 1000));
	const elapsed = seconds >= 86_400
		? new Date(position.fixAtMs).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
		: seconds >= 3600 ? `${Math.floor(seconds / 3600)}h ${Math.floor(seconds / 60) % 60}m ago`
		: seconds < 60 ? `${seconds}s ago` : `${Math.floor(seconds / 60)}m ago`;
	return `${age === "stale" ? "Last seen " : ""}${elapsed} · ±${Math.round(position.accuracyMeters)}m`;
}

interface PreviewRider {
	name: string;
	position: MapFix | null;
	approximate?: boolean;
	/** Raw phone fix retained only as a faint uncertainty boundary. */
	signalPosition?: MapFix | null;
}

/**
 * Swap in an inferred rider position under an otherwise-live map.
 *
 * The rider's raw fix underground (in the Transbay Tube, say) is real but useless — degraded
 * accuracy, or frozen for minutes at the last point the phone had signal. Passing one of these
 * in place of the server's raw position keeps that same "estimated from journey status" marker
 * the follow-only map already uses, without giving up the live driver position, refresh button,
 * or loading/error state that only the data-backed map has.
 */
interface RiderPositionOverride {
	position: MapFix | null;
	approximate: boolean;
	signalPosition?: MapFix | null;
}

interface JourneyOverlay {
	stops: readonly FollowStop[];
	nextStopName: string | null;
}

export function PickupMaps({ deviceId, stationId, nowMs, colorMode, preview, controller, initialView = "location", followOnly = false, riderOverride, journeyOverlay }: { deviceId: string; stationId: string | null; nowMs: number; colorMode: "light" | "dark"; preview?: { position: MapFix | null; rider?: PreviewRider }; controller?: ReturnType<typeof usePickupMap>; initialView?: View; followOnly?: boolean; riderOverride?: RiderPositionOverride | null; journeyOverlay?: JourneyOverlay | null }) {
	useMapLocationRequest(!followOnly);
	const live = usePickupMap(deviceId, !preview && !controller);
	const { data, error, loading, saving, setSpot, refresh } = controller ?? live;
	const mapId = useId();
	const maxAgeMs = LAST_KNOWN_FIX_MS;
	const people = preview
		? { driver: { name: "You", position: mapFix(preview.position, nowMs, maxAgeMs) }, rider: { name: preview.rider?.name ?? "Commuter", position: mapFix(preview.rider?.position, nowMs, maxAgeMs) } }
		: data && riderOverride
			? { ...data, rider: { name: data.rider.name, position: mapFix(riderOverride.position, nowMs, maxAgeMs) } }
			: data;
	const roles: readonly ("driver" | "rider")[] = followOnly ? ["rider"] : preview && !preview.rider ? ["driver"] : ["driver", "rider"];
	const [view, setView] = useState<View>(initialView);
	const [focus, setFocus] = useState<Focus>(followOnly ? "rider" : "both");
	const [follow, setFollow] = useState(true);
	const [mapResizeVersion, setMapResizeVersion] = useState(0);
	const [selected, setSelected] = useState<PickupZoneId | null>(data?.currentZoneId ?? pickupZonesFor(stationId)[0]?.id ?? null);
	const [selectionLabel, setSelectionLabel] = useState<{ id: PickupZoneId; untilMs: number } | null>(null);
	const [tileError, setTileError] = useState(false);
	const dim = colorMode === "dark";
	const host = useRef<HTMLDivElement>(null);
	const mapRef = useRef<L.Map | null>(null);
	const layersRef = useRef<L.LayerGroup | null>(null);
	const framingRef = useRef<string | null>(null);
	const framingSizeRef = useRef(0);
	const touchStart = useRef<{ x: number; y: number } | null>(null);
	const tickMs = Math.floor(nowMs / 15_000) * 15_000;
	const zones = pickupZonesFor(stationId);
	const station = stationId ? BART_STATIONS[stationId] : null;
	const driverPosition = people?.driver.position ?? null;
	const riderPosition = people?.rider.position ?? null;
	const riderApproximate = preview ? preview.rider?.approximate === true : riderOverride?.approximate === true;
	const riderSignalPosition = mapFix(preview ? preview.rider?.signalPosition : riderOverride?.signalPosition, nowMs, maxAgeMs);
	const isPreview = !!preview;
	const currentZoneId = data?.currentZoneId ?? null;
	const spotChangedAtMs = data?.spotChangedAtMs ?? null;
	const pickupId = data?.pickupId ?? null;

	useEffect(() => {
		setSelected(zones.find(zone => zone.id === currentZoneId)?.id ?? zones[0]?.id ?? null);
	}, [isPreview, pickupId, currentZoneId, spotChangedAtMs, stationId]);

	function chooseZone(id: PickupZoneId) {
		if (!preview && (!data || saving)) return;
		if (!zones.some(zone => zone.id === id)) return;
		if (preview) setSelected(id);
		else if (currentZoneId !== id) void setSpot(id);
		setSelectionLabel({ id, untilMs: Date.now() + 1000 });
	}

	useEffect(() => {
		if (!selectionLabel) return;
		const timer = window.setTimeout(() => setSelectionLabel(null), Math.max(0, selectionLabel.untilMs - Date.now()));
		return () => window.clearTimeout(timer);
	}, [selectionLabel]);

	useEffect(() => {
		if (!host.current) return;
		const map = L.map(host.current, {
			zoomControl: false, scrollWheelZoom: false, maxZoom: 21, zoomSnap: 0.1, zoomAnimation: false, fadeAnimation: false,
			gestureHandling: true,
			gestureHandlingOptions: { text: { touch: "", scroll: "", scrollMac: "" } },
		}).fitBounds(zoneBounds(stationId), { padding: [16, 16] });
		mapRef.current = map;
		const tiles = L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
			maxNativeZoom: 19, maxZoom: 21,
			attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> · <a href="https://www.bart.gov/schedules/developers/gtfs">BART data</a>',
		}).addTo(map);
		tiles.on("tileerror", () => setTileError(true));
		tiles.on("tileload", () => setTileError(false));
		layersRef.current = L.layerGroup().addTo(map);
		map.on("dragstart", () => setFollow(false));
		map.on("resize", () => {
			if (framingRef.current?.startsWith("pickup:")) fitPickupArea(map, framingRef.current.slice(7));
			else setMapResizeVersion(version => version + 1);
		});
		const container = host.current;
		const guardTouchDrag = (event: PointerEvent) => { if (event.pointerType === "touch") map.dragging.disable(); };
		const guardTouchStart = () => map.dragging.disable();
		// Touch panning is two-finger only (gestureHandling), and Leaflet runs two fingers through
		// TouchZoom, which never fires dragstart — so detect the pan from the midpoint instead.
		let panStart: ScreenPoint | null = null;
		const midpoint = (event: TouchEvent) => touchMidpoint(Array.from(event.touches, touch => ({ x: touch.clientX, y: touch.clientY })));
		const trackTwoFingerPan = (event: TouchEvent) => {
			const current = midpoint(event);
			if (!current) { panStart = null; return; }
			if (!panStart) { panStart = current; return; }
			if (isTwoFingerPan(panStart, current)) setFollow(false);
		};
		container.addEventListener("pointerdown", guardTouchDrag, { capture: true, passive: true });
		container.addEventListener("touchstart", guardTouchStart, { capture: true, passive: true });
		container.addEventListener("touchstart", trackTwoFingerPan, { passive: true });
		container.addEventListener("touchmove", trackTwoFingerPan, { passive: true });
		container.addEventListener("touchend", trackTwoFingerPan, { passive: true });
		container.addEventListener("touchcancel", trackTwoFingerPan, { passive: true });
		const resize = new ResizeObserver(() => map.invalidateSize());
		resize.observe(host.current);
		return () => {
			resize.disconnect();
			container.removeEventListener("pointerdown", guardTouchDrag, true);
			container.removeEventListener("touchstart", guardTouchStart, true);
			for (const type of ["touchstart", "touchmove", "touchend", "touchcancel"] as const) container.removeEventListener(type, trackTwoFingerPan);
			map.remove(); mapRef.current = null; layersRef.current = null;
		};
	}, []);

	useEffect(() => {
		const layers = layersRef.current;
		if (!layers) return;
		layers.clearLayers();
		// Fixed context, drawn first so it always sits behind zones and position markers, and
		// present on every view/tab rather than only while a journey happens to be tracked.
		addBartNetwork(layers, L);
		L.polygon(COLLEGE_PREP_BOUNDARY.map(point => [point.lat, point.lon] as L.LatLngTuple), {
			color: "#e06d2f",
			weight: 3,
			opacity: 0.9,
			fillColor: "#f09a63",
			fillOpacity: 0.12,
		}).bindTooltip("College Prep campus").addTo(layers);
		if (view === "pickup" && station && !zones.length) {
			L.circleMarker([station.lat, station.lon], { radius: 7, color: "#102f66", fillOpacity: 1 })
				.bindTooltip(`${station.name} station`, { permanent: true }).addTo(layers);
		}
		if (view === "pickup") {
			for (const zone of zones) {
				const current = currentZoneId === zone.id;
				const polygon = L.polygon(zone.boundary.map(point => [point.lat, point.lon] as L.LatLngTuple), {
					color: "#102f66", weight: selected === zone.id ? 4 : 3, fillColor: zone.color,
					fillOpacity: selected === zone.id ? 0.4 : 0, dashArray: current ? undefined : "5 3",
				}).addTo(layers);
				polygon.bindTooltip(`${zone.name}${current ? " (current)" : ""}`, {
					permanent: selectionLabel?.id === zone.id && selectionLabel.untilMs > Date.now(),
				});
				polygon.on("click", () => chooseZone(zone.id));
			}
		}
		for (const role of roles) {
			const person = people?.[role];
			const position = person?.position;
			if (!person || !position || mapFixAge(position, tickMs, maxAgeMs) === "missing" || (view === "pickup" && !area(stationId).contains([position.lat, position.lon]))) continue;
			const color = role === "driver" ? "#d34828" : "#007c91";
			const stale = mapFixAge(position, tickMs, maxAgeMs) === "stale";
			const approximate = role === "rider" && riderApproximate;
			if (approximate && riderSignalPosition && mapFixAge(riderSignalPosition, tickMs, maxAgeMs) !== "missing") {
				L.circle([riderSignalPosition.lat, riderSignalPosition.lon], {
					radius: riderSignalPosition.accuracyMeters,
					color,
					weight: 1,
					opacity: 0.45,
					fillOpacity: 0.04,
					dashArray: "3 5",
				}).bindTooltip(`Phone-reported ±${Math.round(riderSignalPosition.accuracyMeters)}m boundary; marker estimated from journey status`).addTo(layers);
			} else {
				L.circle([position.lat, position.lon], { radius: position.accuracyMeters, color, weight: 1, fillOpacity: 0.1, dashArray: stale ? "4 4" : undefined }).addTo(layers);
			}
			const label = document.createElement("span");
			label.textContent = approximate ? `${person.name}: Estimated from journey status` : `${person.name}: ${fixLabel(position, tickMs, maxAgeMs)}`;
			if (role === "rider" && !stale) {
				L.circleMarker([position.lat, position.lon], {
					radius: 13,
					color: "#1688c8",
					weight: 2,
					opacity: 0.45,
					fillOpacity: 0.08,
					className: "pickup-maps__rider-pulse",
				}).addTo(layers);
			}
			L.circleMarker([position.lat, position.lon], { radius: 8, color: role === "rider" ? "#102f66" : "#fff", weight: 3, dashArray: approximate ? "3 3" : undefined, fillColor: role === "rider" ? "#1688c8" : color, fillOpacity: approximate ? 0.55 : stale ? 0.5 : 1 })
				.bindTooltip(label).addTo(layers);
		}
	}, [data, people, roles, view, selected, selectionLabel, station, stationId, maxAgeMs, tickMs, saving, currentZoneId, riderApproximate, riderSignalPosition]);

	useEffect(() => {
		const map = mapRef.current;
		if (!map) return;
		const framing = view === "pickup" ? `pickup:${stationId}` : `${view}:${focus}:${follow}`;
		const changedFrame = framingRef.current !== framing;
		framingRef.current = framing;
		const resized = framingSizeRef.current !== mapResizeVersion;
		framingSizeRef.current = mapResizeVersion;
		if (view === "pickup") {
			if (!changedFrame) return;
			map.setMaxBounds(area(stationId).pad(0.12));
			fitPickupArea(map, stationId);
			return;
		}
		map.setMaxBounds(undefined);
		map.setMinZoom(2);
		if (!follow) return;
		const positions = (focus === "both" ? [driverPosition, riderPosition] : [focus === "driver" ? driverPosition : riderPosition])
			.filter((position): position is MapFix => position != null && mapFixAge(position, Date.now(), maxAgeMs) !== "missing");
		if (!positions.length) { framingRef.current = null; return; }
		const bounds = L.latLngBounds(positions.map(position => [position.lat, position.lon] as L.LatLngTuple));
		if (changedFrame || resized || (positions.length > 1 && !map.getBounds().contains(bounds))) {
			map.fitBounds(bounds, { padding: [35, 35], maxZoom: changedFrame ? followOnly ? 15 : 17 : map.getZoom() });
		} else {
			map.panTo(bounds.getCenter());
		}
	}, [view, focus, follow, driverPosition, riderPosition, stationId, maxAgeMs, mapResizeVersion, followOnly]);

	function centerPositions() {
		const map = mapRef.current;
		const positions = (focus === "both" ? [driverPosition, riderPosition] : [focus === "driver" ? driverPosition : riderPosition])
			.filter((position): position is MapFix => position != null && mapFixAge(position, Date.now(), maxAgeMs) !== "missing");
		if (!map || !positions.length) return;
		map.fitBounds(L.latLngBounds(positions.map(position => [position.lat, position.lon] as L.LatLngTuple)), {
			padding: [35, 35],
			maxZoom: followOnly ? 15 : 17,
		});
	}

	const currentName = zones.find(zone => zone.id === currentZoneId)?.name;
	const changedBy = data?.spotChangedBy ? data[data.spotChangedBy].name : null;
	return (
		<section className="pickup-maps" aria-label={followOnly ? "Eben location map" : "Pickup maps"} data-dim={dim}>
			{!preview && zones.length > 0 && <div className="pickup-maps__current" role="status">
				<span>{currentName ? "Current pickup spot" : "Default pickup spot"}</span>
				<strong>{loading && !data ? "Loading spot..." : !data ? "Spot unavailable" : currentName ?? zones[0]?.name ?? "Not set"}</strong>
				{changedBy && spotChangedAtMs && <small>Updated by {changedBy} at {new Date(spotChangedAtMs).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</small>}
			</div>}
			{!followOnly && <div className="pickup-maps__tabs" role="tablist" aria-label="Map view"
				onTouchStart={event => { const touch = event.touches[0]; touchStart.current = { x: touch.clientX, y: touch.clientY }; }}
				onTouchEnd={event => { const touch = event.changedTouches[0]; const start = touchStart.current; touchStart.current = null; if (start && Math.abs(touch.clientX - start.x) > 50 && Math.abs(touch.clientY - start.y) < 35) { setView(touch.clientX < start.x ? "pickup" : "location"); } }}>
				{(["location", "pickup"] as const).map(tab => <button key={tab} type="button" role="tab" id={`${mapId}-tab-${tab}`} aria-controls={`${mapId}-panel`} aria-selected={view === tab} tabIndex={view === tab ? 0 : -1}
					onClick={() => setView(tab)} onKeyDown={event => { if (event.key === "ArrowLeft" || event.key === "ArrowRight") { event.preventDefault(); const next = view === "location" ? "pickup" : "location"; setView(next); document.getElementById(`${mapId}-tab-${next}`)?.focus(); } }}>
					{tab === "location" ? <LocateFixed size={17} aria-hidden="true" /> : <MapPin size={17} aria-hidden="true" />}{tab === "location" ? "Location" : "Pickup"}
				</button>)}
			</div>}
			<div id={`${mapId}-panel`} role={followOnly ? undefined : "tabpanel"} aria-labelledby={followOnly ? undefined : `${mapId}-tab-${view}`}>
				<div className="pickup-maps__toolbar">
					{view === "location" ? <>
						{!followOnly && <div className="pickup-maps__focus" role="group" aria-label="Map focus">
							{(roles.length === 1 ? ["driver"] as const : ["driver", "both", "rider"] as const).map(target => <button type="button" key={target} aria-pressed={focus === target || (roles.length === 1 && target === "driver")} aria-label={target === "both" ? "Focus both people" : `Focus ${people?.[target].name ?? target}`} title={target === "both" ? "Focus both people" : `Focus ${people?.[target].name ?? target}`} onClick={() => { setFocus(target); setFollow(true); }}>
								{target === "driver" ? <CarFront size={17} aria-hidden="true" /> : target === "both" ? <Users size={17} aria-hidden="true" /> : <TrainFront size={17} aria-hidden="true" />}
								<span>{target === "both" ? "Both" : people?.[target].name ?? (target === "driver" ? "Driver" : "Commuter")}</span>
							</button>)}
						</div>}
						{followOnly ? <button type="button" className="pickup-maps__center" onClick={centerPositions}><Crosshair size={18} aria-hidden="true" />Center</button> : null}
						<button type="button" className="pickup-maps__follow" aria-label="Follow positions" title={follow ? "Stop following positions" : "Follow positions automatically"} aria-pressed={follow} onClick={() => setFollow(value => !value)}><LocateFixed size={18} aria-hidden="true" /><span>Follow</span></button>
					</> : <strong>{station?.name ?? "No pickup station"}</strong>}
					{!preview && <RefreshButton className="pickup-maps__icon" label="Refresh positions" disabled={saving} busy={loading} onClick={refresh} size={17} />}
				</div>
				<div className="pickup-maps__frame" hidden={view === "pickup" && !station}>
					<div ref={host} className="pickup-maps__canvas" aria-label={view === "location" ? riderApproximate ? "Estimated journey position and phone uncertainty" : "Observed phone positions" : `${station?.name ?? "Station"} pickup area`} />
					<div className="pickup-maps__zoom">
						<button type="button" title="Zoom in" aria-label="Zoom in" onClick={() => mapRef.current?.zoomIn()}><Plus size={19} /></button>
						<button type="button" title="Zoom out" aria-label="Zoom out" onClick={() => mapRef.current?.zoomOut()}><Minus size={19} /></button>
					</div>
					{view === "location" && journeyOverlay?.nextStopName ? <div className="pickup-maps__next"><span>Next stop</span><strong>{journeyOverlay.nextStopName}</strong></div> : null}
				</div>
				{tileError && <p role="status" className="pickup-maps__notice">Street tiles unavailable</p>}
				{(!preview && (loading || error)) || !people ? <p role="status" className="pickup-maps__notice">{loading ? "Loading positions…" : error ?? "Location sharing unavailable"}</p> : <div className="pickup-maps__people">
					{roles.map(role => {
						const position = people[role].position;
						const approximate = role === "rider" && riderApproximate;
						const detail = approximate
							? `Estimated from journey status${riderSignalPosition ? ` · phone ${fixLabel(riderSignalPosition, nowMs, maxAgeMs)}` : ""}`
							: fixLabel(position, nowMs, maxAgeMs);
						return <p key={role}><span className={`pickup-maps__dot pickup-maps__dot--${role}${approximate ? " pickup-maps__dot--approximate" : ""}`} /><strong>{people[role].name}</strong><span>{detail}{view === "pickup" && position && mapFixAge(position, nowMs, maxAgeMs) !== "missing" && !area(stationId).contains([position.lat, position.lon]) ? " · Outside area" : ""}</span></p>;
					})}
				</div>}
				{view === "pickup" && (zones.length ? <div className="pickup-maps__proposal">
					<label htmlFor={`${mapId}-zone`}>Pickup / drop-off spot</label>
					<select id={`${mapId}-zone`} value={selected ?? ""} disabled={!preview && (!data || saving)} onChange={event => chooseZone(event.target.value as PickupZoneId)}>
						<option value="" disabled>Choose a spot</option>
						{zones.map(zone => <option key={zone.id} value={zone.id}>{zone.name}</option>)}
					</select>
					{!preview && <>
					<div className="pickup-maps__actions">
						<span className="pickup-maps__save" role="status">{saving ? "Saving pickup spot..." : currentZoneId ? "Tap a spot to change it." : "Tap a spot to share it."}</span>
						<button type="button" title="Clear spot" aria-label="Clear spot" disabled={!currentZoneId || !data || saving} onClick={() => void setSpot(null)}><X size={17} /></button>
					</div>
					</>}
				</div> : <p className="pickup-maps__notice">{station ? "Station location · Pickup spot not set" : "No pickup station"}</p>)}
			</div>
		</section>
	);
}